/**
 * IANA timezone options for the clinic Time zone picker (Settings → Practice).
 *
 * `Clinic.timezone` is not a display preference — it decides when the reminder
 * tick fires, what period a monthly statement covers and which day a hospital
 * charge lands on. Until this picker existed the column had no writer anywhere
 * in the product, so every clinic sat on the schema default `America/New_York`.
 *
 * Shape of the list: the picker shows every zone the browser knows in ONE
 * searchable list, with the North-American zones a StableTrack clinic actually
 * practices in hand-labelled and grouped first — a 400+ entry raw IANA list
 * buries the dozen answers that are nearly always right, but hiding the rest
 * behind a toggle just makes the exotic clinic hunt for a switch.
 */
export interface TimezoneOption {
  /** The IANA id that gets stored — this is what the API receives. */
  value: string;
  /** Human label, e.g. "Eastern — New York". */
  label: string;
  /** True for the curated North-American shortlist. */
  common: boolean;
}

const COMMON: Array<[string, string]> = [
  ['America/New_York', 'Eastern — New York'],
  ['America/Chicago', 'Central — Chicago'],
  ['America/Denver', 'Mountain — Denver'],
  ['America/Phoenix', 'Mountain (no DST) — Phoenix'],
  ['America/Los_Angeles', 'Pacific — Los Angeles'],
  ['America/Anchorage', 'Alaska — Anchorage'],
  ['Pacific/Honolulu', 'Hawaii — Honolulu'],
  ['America/Toronto', 'Eastern — Toronto'],
  ['America/Winnipeg', 'Central — Winnipeg'],
  ['America/Edmonton', 'Mountain — Edmonton'],
  ['America/Vancouver', 'Pacific — Vancouver'],
  ['America/Halifax', 'Atlantic — Halifax'],
  ['America/St_Johns', 'Newfoundland — St. John’s'],
];

export const COMMON_TIMEZONES: TimezoneOption[] = COMMON.map(([value, label]) => ({
  value,
  label,
  common: true,
}));

/**
 * Every zone this browser knows, curated ones first. `Intl.supportedValuesOf`
 * is Chrome 99+/Safari 15.4+/Firefox 93+; older browsers (and jsdom in some
 * versions) simply get the curated list, which is why the picker never depends
 * on it being present.
 */
export function allTimezoneOptions(): TimezoneOption[] {
  const supported = (Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  }).supportedValuesOf;

  if (typeof supported !== 'function') return COMMON_TIMEZONES;

  let zones: string[];
  try {
    zones = supported('timeZone');
  } catch {
    return COMMON_TIMEZONES;
  }

  const seen = new Set(COMMON_TIMEZONES.map((o) => o.value));
  const rest = zones
    .filter((z) => !seen.has(z))
    .map((value) => ({ value, label: value.replace(/_/g, ' '), common: false }));

  return [...COMMON_TIMEZONES, ...rest];
}

/**
 * The browser's own zone, for the signup payload. Returns `undefined` rather
 * than a guess when the runtime cannot tell us — the backend then leaves the
 * schema default in place.
 */
export function detectBrowserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** Label for a stored value, including one that is not in either list. */
export function timezoneLabel(value: string, options: TimezoneOption[]): string {
  return options.find((o) => o.value === value)?.label ?? value.replace(/_/g, ' ');
}
